import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { consume } from "@lit/context";
import { configContext } from "../contexts";
import type { RatingConfig } from "../models";

/**
 * Editor for the subject's name and application lock toggle.
 * @fires name-changed - When the name input value changes (detail: { name: string }).
 * @fires lock-changed - When the lock toggle is flipped (detail: { locked: boolean }).
 */
@customElement("bab-name-editor")
export class BabNameEditor extends LitElement {
  static styles = css`
    :host {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
      flex-wrap: wrap;
    }

    label[for="name-input"] {
      font-weight: var(--bab-font-weight, 600);
      font-size: 0.875rem;
      color: var(--bab-text);
      white-space: nowrap;
    }

    input {
      flex: 1;
      min-width: 200px;
      max-width: 320px;
      padding: 9px 14px;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      font-size: 0.95rem;
      font-family: inherit;
      color: var(--bab-input-text, #111827);
      background: var(--bab-input-bg, #fff);
      transition: border-color 0.15s, box-shadow 0.15s;
      outline: none;
    }

    input:focus {
      border-color: var(--bab-accent, #4a90e2);
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15);
    }

    input:disabled {
      background: var(--bab-bg, #f0f4f8);
      color: var(--bab-text-secondary, #6b7280);
      cursor: not-allowed;
      border-color: var(--bab-border);
    }

    .toggle-group {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 6px 14px;
      background: var(--bab-card-bg);
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      user-select: none;
      cursor: pointer;
      transition: border-color 0.15s;
    }

    .toggle-group:hover {
      border-color: var(--bab-text-secondary, #6b7280);
    }

    .toggle-label {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 0.7rem;
      font-weight: var(--bab-font-weight, 600);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bab-text-secondary, #6b7280);
      transition: color 0.2s;
    }

    .toggle-label--active {
      color: var(--bab-text);
    }

    .switch {
      position: relative;
      display: inline-block;
      width: 34px;
      height: 18px;
    }

    .switch input {
      opacity: 0;
      width: 0;
      height: 0;
    }

    .slider {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--bab-bg, #e5e7eb);
      border: 1px solid var(--bab-border);
      transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 18px;
      box-sizing: border-box;
    }

    .slider:before {
      position: absolute;
      content: "";
      height: 12px;
      width: 12px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 50%;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
    }

    input:checked + .slider {
      background-color: var(--bab-accent, #4a90e2);
    }

    input:focus-visible + .slider {
      box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.4);
    }

    input:checked + .slider:before {
      transform: translateX(16px);
    }

    .lock-icon {
      width: 13px;
      height: 13px;
      margin-top: -1px;
    }
  `;

  @consume({ context: configContext, subscribe: true })
  @state()
  config!: RatingConfig;

  @property({ type: String })
  name = "";

  @property({ type: Boolean })
  locked = false;

  private _onInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.dispatchEvent(new CustomEvent("name-changed", { detail: { name: value } }));
  }

  private _onToggle(e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    this.dispatchEvent(new CustomEvent("lock-changed", { detail: { locked: checked } }));
  }

  render() {
    return html`
      <label for="name-input">${this.config?.nameLabel ?? "Name"}</label>
      <input
        id="name-input"
        type="text"
        placeholder="Enter name…"
        .value="${this.name}"
        ?disabled="${this.locked}"
        @input="${this._onInput}"
      />

      <label class="toggle-group">
        <span class="toggle-label ${!this.locked ? "toggle-label--active" : ""}">Edit</span>
        <div class="switch">
          <input
            id="lock-toggle"
            type="checkbox"
            .checked="${this.locked}"
            @change="${this._onToggle}"
          />
          <span class="slider"></span>
        </div>
        <span class="toggle-label ${this.locked ? "toggle-label--active" : ""}">
          <svg class="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          Lock
        </span>
      </label>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-name-editor": BabNameEditor;
  }
}
